/**
 * Service for Mollie payment integration
 * Handles subscriptions, payments, and billing
 */

import { getTarifsByProfileType } from './tarifsService';
import { enTetesAuth } from './enTetesAuth';

const API_BASE_URL = `${import.meta.env.VITE_API_BASE_URL}/payments`;

// Cache for plans by profile type
let plansCache = {};
let plansCacheProfileTypeId = null;

// Mollie redirect URL (for callback after payment)
const MOLLIE_REDIRECT_URL = import.meta.env.VITE_MOLLIE_REDIRECT_URL;

/**
 * Helper function to handle API responses
 */
const handleResponse = async (response) => {
    const responseText = await response.text();

    let result;
    try {
        result = JSON.parse(responseText);
    } catch (parseError) {
        console.error('Failed to parse JSON:', parseError);
        throw new Error(`Invalid JSON response: ${responseText}`);
    }

    if (!response.ok) {
        const error = new Error(result.message || `HTTP error! status: ${response.status}`);
        error.status = response.status;
        error.data = result;
        throw error;
    }

    return result;
};

/**
 * Create a new payment for a subscription
 * @param {string} peopleUuid - User's UUID
 * @param {string} planType - 'STARTER', 'PRO' or 'FULL'
 * @param {string} billingPeriod - 'monthly' or 'yearly'
 * @returns {Promise<Object>} Payment details with checkout URL
 */
export const createPayment = async (peopleUuid, planType, billingPeriod = 'monthly') => {
    try {

        const response = await fetch(`${API_BASE_URL}/create-payment`, {
            method: 'POST',
            credentials: 'include',
            headers: enTetesAuth(),
            body: JSON.stringify({
                peopleUuid,
                planType,
                billingPeriod,
            })
        });

        const result = await handleResponse(response);
        return result;
    } catch (error) {
        console.error('Error creating payment:', error);
        throw error;
    }
};

/**
 * Get user's current subscription status
 * @param {string} peopleUuid - User's UUID
 * @returns {Promise<Object>} Subscription details
 */
export const getSubscription = async (peopleUuid) => {
    try {

        const response = await fetch(`${API_BASE_URL}/subscription/${peopleUuid}`, {
            method: 'GET',
            // CETTE LECTURE EST DESORMAIS RESERVEE AU PROPRIETAIRE (ou a un
            // administrateur) : elle rend des donnees de facturation. Sans
            // jeton, le serveur repond 401 et l'ecran d'abonnement se
            // viderait EN SILENCE au lieu de signaler quoi que ce soit.
            headers: enTetesAuth(),
            credentials: 'include'
        });

        const result = await handleResponse(response);
        return result.data || result.DATA;
    } catch (error) {
        console.error('Error fetching subscription:', error);
        throw error;
    }
};

/**
 * Get ALL of a user's active subscriptions (not just the most recent one)
 *
 * Une personne peut désormais cumuler une app autonome et une formule
 * globale : getSubscription() ne rend qu'une ligne (l'abonnement principal),
 * ce qui masque un second abonnement pourtant payé. Utiliser cette fonction
 * pour calculer les droits réels (union des modules de tous les abonnements).
 * @param {string} peopleUuid - User's UUID
 * @returns {Promise<Array>} List of active subscriptions
 */
export const getActiveSubscriptions = async (peopleUuid) => {
    try {

        const response = await fetch(`${API_BASE_URL}/subscriptions/${peopleUuid}`, {
            method: 'GET',
            // SANS CE JETON, LA SIDEBAR ENTIERE SE REDUIT AUX MODULES FREE.
            // La route exige desormais une identite. PreferencesContext
            // absorbe l'echec par un `return []`, et Menu.jsx filtre alors
            // sur une liste vide : un abonne payant verrait la navigation
            // d'un compte gratuit, sans le moindre message.
            headers: enTetesAuth(),
            credentials: 'include'
        });

        const result = await handleResponse(response);
        return result.data || result.DATA || [];
    } catch (error) {
        console.error('Error fetching active subscriptions:', error);
        throw error;
    }
};

/**
 * Codes des modules ouverts à l'utilisateur, calculés par le serveur :
 * Gratuit = tous les modules (avec plafonds), formule payante = sa grille,
 * plus les modules de ses apps autonomes.
 * @param {string} peopleUuid - User's UUID
 * @returns {Promise<string[]>} Codes de modules, vide si indisponible
 */
export const getAllowedModuleCodes = async (peopleUuid) => {
    try {
        const response = await fetch(`${API_BASE_URL}/allowed-modules/${peopleUuid}`, {
            method: 'GET',
            headers: enTetesAuth(),
            credentials: 'include'
        });
        const result = await handleResponse(response);
        const data = result.data || result.DATA || [];
        return Array.isArray(data) ? data : [];
    } catch (error) {
        console.error('Error fetching allowed modules:', error);
        return [];
    }
};

/**
 * Profil, abonnements et limites de chaque module avec la consommation, tels
 * que le serveur les applique (une limite absente = aucune limite).
 * Mis en cache 30 s : la page Limites et les bandeaux de module partagent l'appel.
 * @param {string} peopleUuid - UUID de la personne
 * @returns {Promise<{profileType: Object, subscriptions: Array, hasPaidPlan: boolean, modules: Array}|null>}
 */
let limitsCache = { uuid: null, at: 0, promise: null };
export const getLimitsOverview = async (peopleUuid, { force = false } = {}) => {
    if (!peopleUuid) return null;
    const frais = limitsCache.uuid === peopleUuid && Date.now() - limitsCache.at < 30000;
    if (!force && frais && limitsCache.promise) return limitsCache.promise;

    const promise = (async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/limits/${peopleUuid}`, {
                method: 'GET',
                headers: enTetesAuth(),
                credentials: 'include'
            });
            const result = await handleResponse(response);
            return result.data || result.DATA || null;
        } catch (error) {
            console.error('Error fetching limits overview:', error);
            limitsCache = { uuid: null, at: 0, promise: null };
            return null;
        }
    })();
    limitsCache = { uuid: peopleUuid, at: Date.now(), promise };
    return promise;
};

/**
 * Get user's payment history
 * @param {string} peopleUuid - User's UUID
 * @returns {Promise<Array>} List of payments
 */
export const getPaymentHistory = async (peopleUuid) => {
    try {

        const response = await fetch(`${API_BASE_URL}/history/${peopleUuid}`, {
            method: 'GET',
            // CETTE LECTURE EST DESORMAIS RESERVEE AU PROPRIETAIRE (ou a un
            // administrateur) : elle rend des donnees de facturation. Sans
            // jeton, le serveur repond 401 et l'ecran d'abonnement se
            // viderait EN SILENCE au lieu de signaler quoi que ce soit.
            headers: enTetesAuth(),
            credentials: 'include'
        });

        const result = await handleResponse(response);
        return result.data || result.DATA || [];
    } catch (error) {
        console.error('Error fetching payment history:', error);
        throw error;
    }
};

/**
 * Get invoice data for a specific payment
 * @param {number} paymentId - Payment ID
 * @returns {Promise<Object>} Invoice data
 */
export const getInvoice = async (paymentId) => {
    try {
        const response = await fetch(`${API_BASE_URL}/invoice/${paymentId}`, {
            method: 'GET',
            // CETTE LECTURE EST DESORMAIS RESERVEE AU PROPRIETAIRE (ou a un
            // administrateur) : elle rend des donnees de facturation. Sans
            // jeton, le serveur repond 401 et l'ecran d'abonnement se
            // viderait EN SILENCE au lieu de signaler quoi que ce soit.
            headers: enTetesAuth(),
            credentials: 'include'
        });

        const result = await handleResponse(response);
        return result.data || result.DATA || null;
    } catch (error) {
        console.error('Error fetching invoice:', error);
        throw error;
    }
};

/**
 * Cancel user's subscription
 * @param {string} peopleUuid - User's UUID
 * @param {string} reason - Optional reason for cancellation
 * @returns {Promise<Object>} Cancellation result
 */
export const cancelSubscription = async (peopleUuid, reason = '') => {
    try {
        console.log('Cancelling subscription for:', peopleUuid);

        const response = await fetch(`${API_BASE_URL}/cancel-subscription`, {
            method: 'POST',
            credentials: 'include',
            headers: enTetesAuth(),
            body: JSON.stringify({
                peopleUuid,
                reason,
            })
        });

        const result = await handleResponse(response);
        console.log('Subscription cancelled:', result);
        return result;
    } catch (error) {
        console.error('Error cancelling subscription:', error);
        throw error;
    }
};

/**
 * Toggle auto-renewal for the current user's subscription
 * L'identité est déduite du jeton signé côté serveur : le peopleUuid
 * n'est plus envoyé, il serait ignoré par l'API.
 * @param {boolean} autoRenew - true to enable, false to disable
 * @returns {Promise<Object>} { success, message }
 */
export const setAutoRenew = async (autoRenew) => {
    try {
        const response = await fetch(`${API_BASE_URL}/auto-renew`, {
            method: 'PUT',
            credentials: 'include',
            headers: enTetesAuth(),
            body: JSON.stringify({
                autoRenew: Boolean(autoRenew),
            })
        });

        const result = await handleResponse(response);
        const success = result.success ?? result.SUCCESS;
        const message = result.message ?? result.MESSAGE;

        return { success, message };
    } catch (error) {
        console.error('Error toggling auto-renew:', error);
        return { success: false, message: error.message };
    }
};

/**
 * Get available subscription tarifs from the database
 * @returns {Promise<Array>} List of tarifs
 */
export const getTarifs = async () => {
    try {
        console.log('Fetching tarifs from API');

        const response = await fetch(`${API_BASE_URL}/tarifs`, {
            method: 'GET',
            credentials: 'include'
        });

        const result = await handleResponse(response);
        console.log('Tarifs retrieved:', result);
        return result.data || result.DATA || [];
    } catch (error) {
        console.error('Error fetching tarifs:', error);
        throw error;
    }
};

// Backward compatibility alias
export const getFormules = getTarifs;

/**
 * Simulate a plan change (upgrade or downgrade) with prorata calculation
 * @param {string} peopleUuid - User's UUID
 * @param {string} planType - Target plan code (e.g., 'FULL', 'STARTER')
 * @param {string} billingPeriod - 'monthly' or 'yearly'
 * @returns {Promise<Object>} Simulation result with isUpgrade, isDowngrade, bonusDays, amountToPay, etc.
 */
export const getChangeSimulation = async (peopleUuid, planType, billingPeriod = 'monthly') => {
    try {
        const params = new URLSearchParams({ peopleUuid, planType, billingPeriod });
        const response = await fetch(`${API_BASE_URL}/calculate-change?${params.toString()}`, {
            method: 'GET',
            // SANS CE JETON, LA MODALE AFFICHE DES MONTANTS FAUX. L'appelant
            // traite `simulation || {}` et ouvre quand meme la fenetre avec
            // amountToPay, creditAmount et bonusDays a zero. Des chiffres
            // credibles et faux valent pire qu'un ecran vide : on signe.
            headers: enTetesAuth(),
            credentials: 'include'
        });

        const result = await handleResponse(response);
        return result.data || result.DATA || null;
    } catch (error) {
        console.error('Error fetching change simulation:', error);
        return null;
    }
};

/**
 * Upgrade subscription to a higher plan with prorata credit
 * @param {string} peopleUuid - User's UUID
 * @param {string} newPlanType - New plan type (e.g., 'FULL')
 * @param {string} billingPeriod - 'monthly' or 'yearly'
 * @returns {Promise<Object>} Upgrade result with payment URL or immediate activation
 */
export const upgradeSubscription = async (peopleUuid, newPlanType, billingPeriod = 'monthly') => {
    try {
        console.log('Upgrading subscription:', { peopleUuid, newPlanType, billingPeriod });

        const response = await fetch(`${API_BASE_URL}/upgrade-subscription`, {
            method: 'POST',
            credentials: 'include',
            headers: enTetesAuth(),
            body: JSON.stringify({
                peopleUuid,
                newPlanType,
                billingPeriod,
            })
        });

        const result = await handleResponse(response);
        console.log('Upgrade result:', result);
        return result;
    } catch (error) {
        console.error('Error upgrading subscription:', error);
        throw error;
    }
};

/**
 * Get available credits for a user
 * @param {string} peopleUuid - User's UUID
 * @returns {Promise<Object>} Credits info with total and list
 */
export const getCredits = async (peopleUuid) => {
    try {
        console.log('Fetching credits for:', peopleUuid);

        const response = await fetch(`${API_BASE_URL}/credits/${peopleUuid}`, {
            method: 'GET',
            credentials: 'include'
        });

        const result = await handleResponse(response);
        console.log('Credits retrieved:', result);
        return {
            credits: result.data || result.DATA || [],
            totalCredit: result.totalCredit || result.TOTALCREDIT || 0
        };
    } catch (error) {
        console.error('Error fetching credits:', error);
        throw error;
    }
};

/**
 * Get subscription statistics (admin only)
 * @returns {Promise<Object>} Stats by plan
 */
export const getSubscriptionStats = async () => {
    try {
        const response = await fetch(`${API_BASE_URL}/subscription-stats`, {
            method: 'GET',
            credentials: 'include'
        });

        const result = await handleResponse(response);
        return result.data || result.DATA || {};
    } catch (error) {
        console.error('Error fetching subscription stats:', error);
        throw error;
    }
};

/**
 * Normalize tarif keys from API (handles uppercase/lowercase)
 * @param {Object} tarif - Tarif from API
 * @returns {Object} Normalized tarif
 */
const normalizeTarifKeys = (tarif) => {
    return {
        id: tarif.id ?? tarif.ID,
        code: (tarif.code ?? tarif.CODE ?? '').toUpperCase(),
        name: tarif.name_fr ?? tarif.NAME_FR ?? tarif.name ?? tarif.NAME ?? '',
        name_fr: tarif.name_fr ?? tarif.NAME_FR ?? '',
        name_en: tarif.name_en ?? tarif.NAME_EN ?? '',
        name_nl: tarif.name_nl ?? tarif.NAME_NL ?? '',
        name_de: tarif.name_de ?? tarif.NAME_DE ?? '',
        name_es: tarif.name_es ?? tarif.NAME_ES ?? '',
        name_it: tarif.name_it ?? tarif.NAME_IT ?? '',
        name_pt: tarif.name_pt ?? tarif.NAME_PT ?? '',
        name_el: tarif.name_el ?? tarif.NAME_EL ?? '',
        description: tarif.description_fr ?? tarif.DESCRIPTION_FR ?? tarif.description ?? tarif.DESCRIPTION ?? '',
        description_fr: tarif.description_fr ?? tarif.DESCRIPTION_FR ?? '',
        description_en: tarif.description_en ?? tarif.DESCRIPTION_EN ?? '',
        description_nl: tarif.description_nl ?? tarif.DESCRIPTION_NL ?? '',
        description_de: tarif.description_de ?? tarif.DESCRIPTION_DE ?? '',
        description_es: tarif.description_es ?? tarif.DESCRIPTION_ES ?? '',
        description_it: tarif.description_it ?? tarif.DESCRIPTION_IT ?? '',
        description_pt: tarif.description_pt ?? tarif.DESCRIPTION_PT ?? '',
        description_el: tarif.description_el ?? tarif.DESCRIPTION_EL ?? '',
        price: parseFloat(tarif.finalmonthlyprice ?? tarif.FINALMONTHLYPRICE ?? tarif.monthlyprice ?? tarif.MONTHLYPRICE ?? 0),
        priceYearly: parseFloat(tarif.finalyearlyprice ?? tarif.FINALYEARLYPRICE ?? tarif.yearlyprice ?? tarif.YEARLYPRICE ?? 0),
        popular: tarif.ispopular ?? tarif.ISPOPULAR ?? tarif.isPopular ?? false,
        sortorder: tarif.sortorder ?? tarif.SORTORDER ?? 0,
        _original: tarif
    };
};

/**
 * Get plans for a specific profile type from the database
 * Uses cache to avoid repeated API calls
 * @param {number} profileTypeId - Profile type ID (optional, uses localStorage if not provided)
 * @returns {Promise<Object>} Plans object keyed by code
 */
export const getPlansForProfileType = async (profileTypeId = null) => {
    // Get profile type ID from param or localStorage
    const ptId = profileTypeId || parseInt(localStorage.getItem('userProfileTypeId'), 10);

    if (!ptId) {
        console.warn('No profile type ID available, returning default FREE plan');
        return { FREE: DEFAULT_FREE_PLAN };
    }

    // Return cached plans if same profile type
    if (plansCacheProfileTypeId === ptId && Object.keys(plansCache).length > 0) {
        return plansCache;
    }

    try {
        const tarifs = await getTarifsByProfileType(ptId);
        const plans = {};

        tarifs.forEach(tarif => {
            const normalized = normalizeTarifKeys(tarif);
            plans[normalized.code] = normalized;
        });

        // Always ensure FREE plan exists
        if (!plans.FREE) {
            plans.FREE = DEFAULT_FREE_PLAN;
        }

        // Cache the result
        plansCache = plans;
        plansCacheProfileTypeId = ptId;

        console.log(`[paymentService] Loaded ${Object.keys(plans).length} plans for profile type ${ptId}`);
        return plans;
    } catch (error) {
        console.error('Error fetching plans for profile type:', error);
        return { FREE: DEFAULT_FREE_PLAN };
    }
};

/**
 * Clear the plans cache (call when user changes or logs out)
 */
export const clearPlansCache = () => {
    plansCache = {};
    plansCacheProfileTypeId = null;
};

/**
 * Default FREE plan (fallback)
 */
const DEFAULT_FREE_PLAN = {
    id: 0,
    code: 'FREE',
    name: 'Free',
    name_fr: 'Gratuit',
    name_en: 'Free',
    name_nl: 'Gratis',
    name_de: 'Kostenlos',
    name_es: 'Gratuito',
    name_it: 'Gratuito',
    name_pt: 'Gratuito',
    name_el: 'Δωρεάν',
    description: '',
    description_fr: '',
    description_en: '',
    description_nl: '',
    description_de: '',
    description_es: '',
    description_it: '',
    description_pt: '',
    description_el: '',
    price: 0,
    priceYearly: 0,
    popular: false,
    sortorder: 0
};

/**
 * DEPRECATED: Use getPlansForProfileType() instead
 * This is kept for backward compatibility but will return empty
 * @deprecated
 */
export const PLANS = {};

/**
 * Get price for a plan based on billing period
 * @param {Object} plan - Plan object (from getPlansForProfileType)
 * @param {string} billingPeriod - 'monthly' or 'yearly'
 * @returns {number} Price
 */
export const getPlanPrice = (plan, billingPeriod = 'monthly') => {
    if (!plan) return 0;
    return billingPeriod === 'yearly' ? (plan.priceYearly || 0) : (plan.price || 0);
};

/**
 * Get savings percentage for yearly billing
 * @param {Object} plan - Plan object (from getPlansForProfileType)
 * @returns {number} Savings percentage
 */
export const getYearlySavings = (plan) => {
    if (!plan || !plan.price || plan.price === 0) return 0;
    const monthlyTotal = plan.price * 12;
    const savings = ((monthlyTotal - (plan.priceYearly || 0)) / monthlyTotal) * 100;
    return Math.round(savings);
};

/**
 * Get the next upgrade plan from a plans object
 * @param {string} currentPlanCode - Current plan code
 * @param {Object} plans - Plans object from getPlansForProfileType
 * @returns {Object|null} Next plan or null if already at max
 */
export const getUpgradePlan = (currentPlanCode, plans) => {
    if (!plans || !currentPlanCode) return null;

    const plansArray = Object.values(plans).sort((a, b) => (a.price || 0) - (b.price || 0));
    const currentIndex = plansArray.findIndex(p => p.code === currentPlanCode.toUpperCase());

    if (currentIndex === -1 || currentIndex >= plansArray.length - 1) {
        return null;
    }

    return plansArray[currentIndex + 1];
};
